import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchClients } from "../services/clientService";
import useUrlState, { param, yesNo } from "./useUrlState";

export const SORTS = ["name", "last_visit", "lifetime_value", "visits", "created"];
export const CONSENT_PURPOSES = ["marketing", "transactional", "data_processing", "photography"];

/*
 * Filters and sort live in the URL (see useUrlState); the cursor does not —
 * a cursor is a position in one moment's result, so returning to the list
 * lands on its first page with the same filters.
 */
const SCHEMA = {
  search: param.string("", { debounce: 400 }),
  tags: param.list(),
  branch: param.string(),
  notVisitedSince: param.date(),
  consent: param.oneOf(CONSENT_PURPOSES),
  consentGranted: param.oneOf(["yes", "no"], "yes"),
  registered: yesNo(),
  // "" = the business's main currency (the API's default); "all" = every currency.
  currency: param.string(),
  sort: param.oneOf(SORTS, "name"),
  direction: param.oneOf(["asc", "desc"], "asc"),
};

/**
 * BIZ-15's list state.
 *
 * ## Why there is no page number here
 *
 * The API pages this list by cursor, so there is no page 4 to jump to — only
 * the row after the last one shown. The hook therefore keeps an opaque cursor
 * and the two the API hands back, and "previous" means the cursor before this
 * page rather than a number one lower.
 *
 * ## Why the total is asked for once
 *
 * A cursor cannot count, and the count is the expensive half — so the total is
 * requested only on the first page of a filter set, then carried while the user
 * pages through it. Changing a filter resets both, because the number would
 * otherwise describe a list nobody is looking at any more.
 */
export default function useClients() {
  const { t, i18n } = useTranslation();

  const url = useUrlState(SCHEMA, { pageKey: null });
  const { values, set, draft, setDraft, flush } = url;
  const { sort, direction } = values;
  const filters = useMemo(() => {
    const { sort: _sort, direction: _direction, ...rest } = values;
    return rest;
  }, [values]);

  // The cursor and the total each belong to the list they were read from. A
  // new filter set (or, for the cursor, a new sort) simply has none yet — no
  // reset step, so the first fetch after a change can never use a stale one.
  const filterKey = JSON.stringify(filters);
  const listKey = `${filterKey}|${sort}|${direction}`;

  const [position, setPosition] = useState({ key: listKey, cursor: null });
  const cursor = position.key === listKey ? position.cursor : null;

  const [counted, setCounted] = useState({ key: null, total: null });
  const total = counted.key === filterKey ? counted.total : null;
  // Read inside load without making the count itself a reason to reload.
  const countedKey = useRef(counted.key);
  countedKey.current = counted.key;

  const [clients, setClients] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { clients: list, meta: pagination } = await fetchClients({
        cursor,
        filters,
        sort,
        direction,
        withTotal: countedKey.current !== filterKey,
      });

      setClients(list);
      setMeta(pagination);

      if (pagination?.total != null) setCounted({ key: filterKey, total: pagination.total });
    } catch (err) {
      setError(apiErrorMessage(err, t("clients.loadFailed")));
      setClients([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, filterKey, sort, direction, i18n.language, t]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = useCallback((changes) => set(changes), [set]);

  /** Every filter back to its default; the sort is kept, as before. */
  const resetFilters = useCallback(
    () => set(Object.fromEntries(Object.keys(filters).map((key) => [key, SCHEMA[key].fallback]))),
    [set, filters]
  );

  const applySort = useCallback(
    (nextSort, nextDirection) => set({ sort: nextSort, direction: nextDirection }),
    [set]
  );

  const goNext = useCallback(() => {
    if (meta?.next_cursor) setPosition({ key: listKey, cursor: meta.next_cursor });
  }, [meta, listKey]);

  const goPrevious = useCallback(() => {
    if (meta?.prev_cursor) setPosition({ key: listKey, cursor: meta.prev_cursor });
  }, [meta, listKey]);

  const setSearchTerm = useCallback((text) => setDraft("search", text), [setDraft]);
  const submitSearch = useCallback(() => flush("search"), [flush]);

  return {
    clients,
    meta,
    total,
    filters,
    sort,
    direction,
    applyFilters,
    resetFilters,
    applySort,
    searchTerm: draft.search ?? "",
    setSearchTerm,
    submitSearch,
    goNext,
    goPrevious,
    // The first page is the one the API offers no way back from.
    atStart: !meta?.prev_cursor,
    atEnd: !meta?.next_cursor,
    loading,
    error,
    reload: load,
  };
}
